/**
 * Custom homepage for Merrily
 * Replaces the default LandingPage with marketplace-specific content
 */

import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';

import { hasUserSearched, logFunnelEvent, autoDetectLocation, radiusToBounds } from '../../util/analytics';
import { SEARCH_RADIUS_MILES } from '../../util/search';
import { extractZip, lookupZipCentroid } from '../../util/usZip';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { Page, LayoutSingleColumn } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import HeroSearchSection from './HeroSearchSection/HeroSearchSection';
import HowItWorksSection from './HowItWorksSection/HowItWorksSection';
import FeaturedListingsSection from './FeaturedListingsSection/FeaturedListingsSection';
import TrustSection from './TrustSection/TrustSection';
import FreePartiesBanner from './FreePartiesBanner/FreePartiesBanner';
// CategoryGridSection retired 2026-07-02 (occasion tiles replaced by
// Featured kits — see render note); ProviderCTASection import was dead.

import css from './RentEventHomePage.module.css';

export const RentEventHomePageComponent = (props) => {
  const { history, location, featuredListings = [] } = props;
  const config = useConfiguration();
  const [isReturningUser, setIsReturningUser] = useState(false);

  useEffect(() => {
    // Log page view
    logFunnelEvent('page_view_home');

    // Check if user has searched before (affects hero display)
    setIsReturningUser(hasUserSearched());

    // Auto-detect location on first visit (triggers browser geolocation prompt)
    autoDetectLocation();
  }, []);

  const handleSearch = async (searchParams) => {
    // Build search URL with parameters
    const searchQuery = new URLSearchParams();

    if (searchParams.location) {
      // Geocode the ZIP via the local centroid table and send real `bounds`
      // so the hero location actually filters results. Previously only a
      // dead `address` param was sent and every ZIP returned the same kits
      // (UI/UX audit P1). The box mirrors the SearchPage LocationFilter:
      // query SEARCH_RADIUS_MILES wide, results split at NEAR_RADIUS_MILES
      // (docs/design/search-extended-radius.md).
      const zip = extractZip(searchParams.location.fullName);
      const centroid = zip ? await lookupZipCentroid(zip).catch(() => null) : null;
      if (centroid) {
        const { ne, sw } = radiusToBounds(centroid.lat, centroid.lng, SEARCH_RADIUS_MILES);
        searchQuery.set('bounds', `${ne},${sw}`);
        const label = centroid.city ? `${centroid.city}, ${centroid.state} ${zip}` : zip;
        searchQuery.set('address', label);
      } else {
        // Unknown ZIP or free text: keep the old pass-through so the search
        // still runs; the SearchPage Location filter can refine from there.
        searchQuery.set('address', searchParams.location.fullName);
      }
    }
    
    if (searchParams.category) {
      // Map homepage category keys to pub_event_type values
      // (matches the extended data field stored on listings)
      const categoryToTheme = {
        birthday: 'birthday',
        bridal: 'bridal',
        baby_shower: 'baby_shower',
        milestone: 'milestone',
        themed_parties: 'themed_parties',
        beach_day: 'beach_day',
        holiday: 'holiday',
        cultural: 'cultural',
      };
      const themeCategory = categoryToTheme[searchParams.category] || searchParams.category;
      // Stream 3 PR-4i: occasion is now a multi-value field. Filter
      // by pub_occasions so kits tagged with this occasion as
      // primary OR secondary all appear (OR matching, not AND).
      // pub_categoryLevel1 still works for back-compat (mirrored to
      // the primary occasion), but pub_occasions is the canonical
      // path forward.
      searchQuery.set('pub_occasions', themeCategory);
    }
    
    if (searchParams.dates) {
      searchQuery.set('dates', `${searchParams.dates.start},${searchParams.dates.end}`);
    }

    // Navigate to search page
    history.push(`/s?${searchQuery.toString()}`);
  };

  // Brand suffix is appended centrally in Page.js — keep it out of the
  // literal so it stays single + consistent across environments.
  const title = "Party Kit Rentals in Santa Barbara";
  const description = "Rent themed party kits from your neighbors in Santa Barbara, Goleta, Carpinteria, and Montecito. Cheaper than buying. Delivered to your door. Browse dinosaur, princess, superhero themes and more.";

  // Page.js already emits the base Organization node sitewide (@id
  // "<rootURL>#organization", with name/logo/sameAs). Keying this node to the
  // SAME @id makes crawlers merge the properties instead of seeing a second
  // organization — so this only ADDS description + the service area.
  // (The previous standalone node pointed url at REACT_APP_CANONICAL_ROOT_URL,
  // an env var that doesn't exist — it rendered "url": undefined.)
  const schema = {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    '@id': `${config.marketplaceRootURL}#organization`,
    description,
    areaServed: ['Santa Barbara, CA', 'Goleta, CA', 'Carpinteria, CA', 'Montecito, CA'],
  };

  return (
    <Page
      className={css.root}
      title={title}
      description={description}
      schema={schema}
      scrollingDisabled={false}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.main}>
          {/* 5 free parties giveaway (2026-09-12); self-hides after Oct 12. */}
          <FreePartiesBanner />

          <HeroSearchSection onSearch={handleSearch} />

          {/* Featured kits REPLACES the occasion tile grid (owner decision
              2026-07-02): the hero already offers occasion entry via the
              dropdown + quick pills, and with a small catalog the 8 tiles
              mostly dead-ended. Real, bookable product within one scroll.
              Bring back a compact occasion row when supply earns it. */}
          <FeaturedListingsSection listings={featuredListings} />

          <HowItWorksSection
            onBrowse={() => history.push('/s')}
            onListKit={() => history.push('/become-a-vendor/get-started')}
          />

          <TrustSection onBrowse={() => history.push('/s')} />
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

// defaultProps removed — using JS default params

RentEventHomePageComponent.propTypes = {};

const mapStateToProps = state => {
  const { featuredListingRefs = [] } = state.RentEventHomePage || {};
  return {
    featuredListings: getListingsById(
      state,
      featuredListingRefs.map(ref => ref.id)
    ),
  };
};

const mapDispatchToProps = dispatch => ({});

const RentEventHomePage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(RentEventHomePageComponent);

export default RentEventHomePage;